import { Box, Typography } from "@mui/material";
import type { ChangeEvent, DragEvent, KeyboardEvent } from "react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ACCEPTED_IMAGE_INPUT, isAcceptedImageFile } from "../common/imageUpload";

interface ImageUploaderProps {
  onFileChange: (file: File[]) => void;
}

export default function ImageUploader({ onFileChange }: ImageUploaderProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFiles = (selectedFiles: FileList | null) => {
    const imageFile = Array.from(selectedFiles ?? []).find(isAcceptedImageFile);

    if (imageFile) {
      onFileChange([imageFile]);
    }
  };

  const openFilePicker = () => {
    inputRef.current?.click();
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleFiles(event.target.files);
    event.target.value = "";
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!isDragging) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    handleFiles(event.dataTransfer.files);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openFilePicker();
    }
  };

  return (
    <Box>
      <Box
        data-test-id="uploader"
        role="button"
        tabIndex={0}
        aria-label={t("drag_and_drop")}
        onClick={openFilePicker}
        onKeyDown={handleKeyDown}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          alignItems: "center",
          border: "2px dashed",
          borderColor: isDragging ? "secondary.main" : "rgba(0, 0, 0, 0.12)",
          borderRadius: 1,
          cursor: "pointer",
          display: "flex",
          height: 250,
          justifyContent: "center",
          outline: "none",
          px: 3,
          textAlign: "center",
          transition: "border-color 0.2s ease, background-color 0.2s ease",
          "&:focus-visible": {
            borderColor: "secondary.main",
            boxShadow: (theme) => `0 0 0 3px ${theme.palette.secondary.light}`,
          },
          ...(isDragging && {
            backgroundColor: "action.hover",
          }),
        }}
      >
        <Typography
          sx={{
            fontSize: "1.5rem",
            lineHeight: 1.334,
          }}
        >
          {t("drag_and_drop")}
        </Typography>
      </Box>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_IMAGE_INPUT}
        hidden
        onChange={handleInputChange}
      />
    </Box>
  );
}
